import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Rocket } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { create } from "zustand";

import { api } from "../../api/client";
import type { PromotionStatus, PromotionSummary } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { PropRow } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Button } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { isPreprod, useEnvironmentValue } from "../environment";
import { BackupButton, BackupLine } from "./BackupDialog";
import { MaintenanceButton, MaintenanceLine } from "./MaintenanceDialog";
import { PromotionDialog } from "./PromotionDialog";
import {
  blockerText,
  formatBuild,
  isActive,
  isFinal,
  isRollingBack,
  markOutcomeSeen,
  outcomeTone,
  panelBlocker,
  panelStatus,
  POLL_CLOSED_MS,
  pollDelay,
  PROMOTION_KEY,
  reasonText,
  recentOutcome,
  seenOutcome,
  STEP_ORDER,
  stepNumber,
  trackRequest,
  useSeenOutcome,
  useTrackedRequest,
} from "./promotion";
import { Waiting } from "../../components/Waiting";

/** Encadré « Promotion en production » de l'accueil (registre F35) : préproduction seulement, au-dessus des dossiers.
 *  Il montre la version de la préproduction et celle de la production, ce qui empêche la promotion (en clair, jamais
 *  en info-bulle, blocage après cinq mots de passe faux compris), une demande dont le mot de passe est en cours de
 *  vérification, une promotion en cours (« Suivre », ou le retour arrière en cours après un échec), le résultat de
 *  la dernière promotion finie pendant les 24 h qui suivent (rappel masquable, « Détail » rouvre la fenêtre dessus)
 *  et une production qui n'a pas pu être rétablie (encadré rouge permanent). « Promouvoir… » reste un bouton
 *  secondaire : l'appel principal de l'accueil ne change pas. En production, rien n'est rendu et l'API de la
 *  promotion n'est jamais appelée. */

export function PromotionSlot() {
  const preprod = useEnvironmentValue(isPreprod);
  return preprod ? <PromotionPanel /> : null;
}

/** Fenêtre ouverte ou non : l'encadré est dans la colonne de l'accueil, qui glisse à l'apparition (transform) ; la
 *  fenêtre est posée hors de cette colonne (`PromotionDialogHost`, à la racine de l'accueil), sans quoi son voile
 *  serait borné à la colonne au lieu de couvrir tout l'écran. */
const useDialog = create<{ open: boolean; shown: PromotionStatus | null }>(() => ({ open: false, shown: null }));
const setDialogOpen = (open: boolean, shown: PromotionStatus | null = null) => useDialog.setState({ open, shown });

/** Fenêtre de la promotion, à poser à la racine de l'accueil (préproduction seulement). */
export function PromotionDialogHost() {
  const preprod = useEnvironmentValue(isPreprod);
  const open = useDialog((s) => s.open);
  const shownStatus = useDialog((s) => s.shown);
  // Résumé lu par l'encadré (même clé) : jamais demandé ici.
  const summary = useQuery<PromotionSummary>({ queryKey: PROMOTION_KEY, queryFn: api.preprod.promotion, enabled: false });
  useEffect(() => () => setDialogOpen(false), []);
  const shown = preprod && open && Boolean(summary.data);
  return (
    <Presence show={shown}>
      {shown && summary.data && (
        <PromotionDialog summary={summary.data} shown={shownStatus} onClose={() => setDialogOpen(false)} />
      )}
    </Presence>
  );
}

export function PromotionPanel() {
  const dialogOpen = useDialog((s) => s.open);
  const summary = useQuery<PromotionSummary>({
    queryKey: PROMOTION_KEY,
    queryFn: api.preprod.promotion,
    // Fenêtre fermée pendant une promotion : relu toutes les 5 s ; fenêtre ouverte, elle suit elle-même le statut.
    refetchInterval: (query) => (dialogOpen ? false : (pollDelay(query.state.data?.status?.state, false) ?? false)),
    refetchOnWindowFocus: true,
  });
  // Demande déposée depuis ce poste et pas encore publiée (ou plus récente que le statut publié) : montrée à la place
  // du résultat précédent ; fenêtre fermée en cours de route, son statut est relu ici jusqu'à la fin.
  const tracked = useTrackedRequest((s) => s.status);
  const status = panelStatus(summary.data?.status, tracked);
  const follow = !dialogOpen && tracked !== null && status === tracked && isActive(tracked.state);
  useEffect(() => {
    if (!follow || !tracked) return;
    let stop = false;
    const timer = window.setTimeout(async () => {
      try {
        const reply = await api.preprod.promotionStatus(tracked.request_id);
        if (stop) return;
        trackRequest(reply.status);
        if (!reply.status || isFinal(reply.status.state)) void summary.refetch();
      } catch {
        // relevé manqué (serveur occupé, réseau) : nouvel essai au tour suivant
        if (!stop) trackRequest({ ...tracked });
      }
    }, POLL_CLOSED_MS);
    return () => {
      stop = true;
      window.clearTimeout(timer);
    };
  }, [follow, tracked]);
  return (
    <PromotionCard
      summary={summary.data && { ...summary.data, status }}
      failed={summary.isError}
      onRetry={() => void summary.refetch()}
      onOpen={(shown) => setDialogOpen(true, shown ?? null)}
      maintenanceAction={<MaintenanceButton />}
      maintenanceRow={<MaintenanceLine />}
      backupAction={<BackupButton />}
      backupRow={<BackupLine />}
    />
  );
}

/** Carte de l'encadré, sans requête (rendue telle quelle par les essais). */
export function PromotionCard({
  summary,
  failed = false,
  onRetry,
  onOpen,
  maintenanceAction,
  maintenanceRow,
  backupAction,
  backupRow,
}: {
  summary: PromotionSummary | undefined;
  failed?: boolean;
  onRetry?: () => void;
  /** « Annoncer une maintenance… » (registre F40), à côté de « Promouvoir… », et la ligne de l'annonce publiée. */
  maintenanceAction?: ReactNode;
  maintenanceRow?: ReactNode;
  /** « Sauvegarder Generex-Web… » (registre F43), même rangée, et la ligne de la dernière sauvegarde complète. */
  backupAction?: ReactNode;
  backupRow?: ReactNode;
  /** Ouvre la fenêtre : sur la saisie ou la promotion en cours, ou sur un résultat (`shown`, « Détail »). */
  onOpen: (shown?: PromotionStatus) => void;
}) {
  const status = summary?.status ?? null;
  const active = isActive(status?.state);
  const rollingBack = isRollingBack(status);
  const blocker = summary ? panelBlocker(summary) : null;
  const unrestored = status?.state === "failed_unrestored";
  // Rappel du dernier résultat (24 h, masquable sur ce poste) : promotion finie fenêtre fermée ; masqué dès qu'il a
  // été vu (croix, ou fenêtre fermée sur lui).
  const seen = useSeenOutcome((s) => s.seen) ?? seenOutcome();
  const outcome = recentOutcome(status, Date.now(), seen);
  const hide = (requestId: string) => markOutcomeSeen(requestId);
  return (
    <Motion
      origin="fade"
      data-promotion-panel
      role="region"
      aria-label={t("Promotion en production")}
      className="mb-8 rounded-xl border border-line bg-surface shadow-card"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-title font-semibold text-ink">{t("Promotion en production")}</h2>
          <p className="mt-0.5 text-meta leading-snug text-ink-muted">
            {t("La version de la préproduction remplace celle de la production. La production est sauvegardée d'abord et rétablie d'elle-même en cas d'échec.")}
          </p>
        </div>
        {backupAction}
        {maintenanceAction}
        {summary &&
          (active ? (
            <Button variant="secondary" onClick={() => onOpen(status ?? undefined)}>
              <ArrowUpRight size={14} /> {t("Suivre")}
            </Button>
          ) : (
            <Button
              variant="secondary"
              data-attention="promotion-open"
              blocked={Boolean(blocker)}
              onBlocked={() => designate(attentionSelector("promotion-blocker"), "ring")}
              onClick={() => onOpen()}
            >
              <Rocket size={14} /> {t("Promouvoir…")}
            </Button>
          ))}
      </div>

      <div className="border-t border-line px-5 pt-1 pb-3">
        {failed ? (
          <Callout
            tone="danger"
            className="mt-2"
            action={
              <Button variant="secondary" size="sm" onClick={onRetry}>
                {t("Réessayer")}
              </Button>
            }
          >
            {t("Les versions de la préproduction et de la production n'ont pas pu être lues.")}
          </Callout>
        ) : !summary ? (
          <p className="flex min-h-8 items-center py-1.5 text-ui text-ink-muted">{<Waiting>{t("Lecture des versions…")}</Waiting>}</p>
        ) : (
          <>
            <PropRow label={t("En préproduction")}>{t("Version du {date}", { date: formatBuild(summary.preprod.build) })}</PropRow>
            <PropRow label={t("En production")}>
              {summary.production.reachable && summary.production.build
                ? t("Version du {date}", { date: formatBuild(summary.production.build) })
                : t("Ne répond pas")}
            </PropRow>
            {maintenanceRow}
            {backupRow}
            {active && !rollingBack && (
              <p role="status" className="flex min-h-8 items-center py-1.5 text-ui font-medium text-run">
                {status?.state === "pending"
                  ? <Waiting>{t("Vérification du mot de passe de promotion…")}</Waiting>
                  : t("Promotion en cours, étape {n} sur {total}", { n: stepNumber(status), total: STEP_ORDER.length })}
              </p>
            )}
            {rollingBack && (
              <p role="alert" className="flex min-h-8 items-center py-1.5 text-ui font-medium text-danger">
                {t("Échec : retour à la version précédente en cours")}
              </p>
            )}
          </>
        )}
        <Presence show={Boolean(blocker) && !unrestored}>
          {blocker && !unrestored && (
            <div data-attention="promotion-blocker" className="mt-2 rounded-lg">
              <Callout tone={blocker === "locked" ? "warn" : "info"}>{blockerText(blocker, summary?.locked_until)}</Callout>
            </div>
          )}
        </Presence>
        <Presence show={Boolean(outcome)}>
          {outcome && <OutcomeNotice status={outcome} onDetail={() => onOpen(outcome)} onHide={() => hide(outcome.request_id)} />}
        </Presence>
        <Presence show={unrestored}>
          {unrestored && (
            <Callout tone="danger" className="mt-2" title={t("Production à rétablir")}>
              {reasonText(status?.reason, status)}{" "}
              {t("La production n'a pas pu être rétablie d'elle-même : intervention nécessaire depuis le Terminal du Mac (mode d'emploi d'exploitation, « Promotion interrompue »).")}
            </Callout>
          )}
        </Presence>
      </div>
    </Motion>
  );
}

/** Rappel du résultat d'une promotion finie pendant que la fenêtre était fermée. */
function OutcomeNotice({ status, onDetail, onHide }: { status: PromotionStatus; onDetail: () => void; onHide: () => void }) {
  const before = formatBuild(status.production_before);
  const title =
    status.state === "succeeded"
      ? t("Version du {date} en production", { date: formatBuild(status.production_after ?? status.preprod_build) })
      : status.state === "failed_restored"
        ? t("Promotion échouée")
        : status.state === "interrupted_restored"
          ? t("Promotion interrompue")
          : status.state === "refused"
            ? t("Promotion refusée")
            : t("Promotion abandonnée");
  const text =
    status.state === "succeeded"
      ? t("La sauvegarde de la production d'avant la promotion est gardée.")
      : status.state === "failed_restored"
        ? `${reasonText(status.reason, status)} ${t("La production est revenue à la version du {date}. Au plus les saisies de la dernière minute sont perdues.", { date: before })}`
        : status.state === "interrupted_restored"
          ? t("La promotion a été interrompue (arrêt ou redémarrage du serveur) : la production est revenue à la version du {date}.", { date: before })
          : `${status.reason ? `${reasonText(status.reason, status)} ` : ""}${t("La production n'a pas changé.")}`;
  return (
    <div data-promotion-outcome className="mt-2">
      <Callout
        tone={outcomeTone(status.state)}
        title={title}
        onClose={onHide}
        action={
          <Button variant="secondary" size="sm" onClick={onDetail}>
            {t("Détail")}
          </Button>
        }
      >
        {text}
      </Callout>
    </div>
  );
}
